import { apply, Matrix } from "./matrix";
import { pathBounds } from "./path";
import { ImportNode } from "./types";

// Axis-aligned rectangular clips in root space: exactly what exporters emit for cropped photos and
// frames. Images are cropped natively; other shapes are dropped when fully outside the clip.

export interface ClipRect {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

const EPSILON = 0.01;
const AXIS_EPSILON = 1e-6;

export const intersectClip = (a: ClipRect | null, b: ClipRect | null): ClipRect | null => {
  if (!a) return b;
  if (!b) return a;
  return {
    minX: Math.max(a.minX, b.minX),
    minY: Math.max(a.minY, b.minY),
    maxX: Math.min(a.maxX, b.maxX),
    maxY: Math.min(a.maxY, b.maxY),
  };
};

/** The root-space rect of a local rect, or null when the matrix rotates or skews it. */
export const clipFromRect = (m: Matrix, x: number, y: number, w: number, h: number): ClipRect | null => {
  if (Math.abs(m.b) > AXIS_EPSILON || Math.abs(m.c) > AXIS_EPSILON) return null;
  const p = apply(m, { x, y });
  const q = apply(m, { x: x + w, y: y + h });
  return { minX: Math.min(p.x, q.x), minY: Math.min(p.y, q.y), maxX: Math.max(p.x, q.x), maxY: Math.max(p.y, q.y) };
};

const isEmpty = (c: ClipRect): boolean => c.maxX - c.minX <= EPSILON || c.maxY - c.minY <= EPSILON;

const contains = (clip: ClipRect, b: ClipRect): boolean =>
  b.minX >= clip.minX - EPSILON &&
  b.minY >= clip.minY - EPSILON &&
  b.maxX <= clip.maxX + EPSILON &&
  b.maxY <= clip.maxY + EPSILON;

const boundsOf = (node: ImportNode): ClipRect | null => {
  switch (node.kind) {
    case "path":
      return pathBounds(node.segments);
    case "rect":
    case "ellipse":
    case "image":
      return Math.abs(node.rotation) < AXIS_EPSILON
        ? { minX: node.x, minY: node.y, maxX: node.x + node.w, maxY: node.y + node.h }
        : null;
    default:
      return null;
  }
};

export type ClipOutcome = { readonly node: ImportNode | null; readonly ignored: boolean };

/** Applies a clip to a leaf node: crop images and square rects, drop what is fully hidden. */
export const applyClip = (node: ImportNode, clip: ClipRect | null): ClipOutcome => {
  if (!clip || node.kind === "text" || node.kind === "group") return { node, ignored: false };
  if (isEmpty(clip)) return { node: null, ignored: false };
  const bounds = boundsOf(node);
  if (!bounds) return { node, ignored: true };
  const visible = intersectClip(clip, bounds) as ClipRect;
  if (isEmpty(visible)) return { node: null, ignored: false };
  if (contains(clip, bounds)) return { node, ignored: false };
  const box = { x: visible.minX, y: visible.minY, w: visible.maxX - visible.minX, h: visible.maxY - visible.minY };
  if (node.kind === "image") {
    if (node.crop) return { node, ignored: true };
    const fx = (v: number): number => (v - node.x) / node.w;
    const fy = (v: number): number => (v - node.y) / node.h;
    const crop = {
      topLeft: { x: fx(visible.minX), y: fy(visible.minY) },
      bottomRight: { x: fx(visible.maxX), y: fy(visible.maxY) },
    };
    return { node: { ...node, ...box, crop }, ignored: false };
  }
  if (node.kind === "rect" && node.radius === 0) return { node: { ...node, ...box }, ignored: false };
  return { node, ignored: true };
};
